import { motion } from "framer-motion";
import { useState } from "react";
import { useWindowSize } from "@/hooks/useWindowSize";

/* ── Animated SVG X Symbol ── */
const SymbolX = ({ size = 80 }) => {
  const s = size * 0.7;
  const pad = size * 0.15;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Glow filter */}
      <defs>
        <filter id="glow-x" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <linearGradient id="grad-x" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFD700" />
          <stop offset="50%" stopColor="#FFA500" />
          <stop offset="100%" stopColor="#FFD700" />
        </linearGradient>
      </defs>
      {/* Line 1 */}
      <motion.line
        x1={pad} y1={pad} x2={pad + s} y2={pad + s}
        stroke="url(#grad-x)" strokeWidth={size * 0.12} strokeLinecap="round"
        filter="url(#glow-x)"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      />
      {/* Line 2 */}
      <motion.line
        x1={pad + s} y1={pad} x2={pad} y2={pad + s}
        stroke="url(#grad-x)" strokeWidth={size * 0.12} strokeLinecap="round"
        filter="url(#glow-x)"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.15, ease: "easeOut" }}
      />
    </svg>
  );
};

/* ── Animated SVG O Symbol ── */
const SymbolO = ({ size = 80 }) => {
  const cx = size / 2;
  const r = size * 0.32;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <defs>
        <filter id="glow-o" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <linearGradient id="grad-o" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF6B35" />
          <stop offset="50%" stopColor="#FF3366" />
          <stop offset="100%" stopColor="#FF6B35" />
        </linearGradient>
      </defs>
      <motion.circle
        cx={cx} cy={cx} r={r}
        fill="none"
        stroke="url(#grad-o)" strokeWidth={size * 0.1} strokeLinecap="round"
        filter="url(#glow-o)"
        initial={{ pathLength: 0, opacity: 0, rotate: -90 }}
        animate={{ pathLength: 1, opacity: 1, rotate: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        style={{ transformOrigin: "center" }}
      />
    </svg>
  );
};

/* ── Sparkle particles on placement ── */
const PlaceParticles = () => {
  const particles = Array.from({ length: 6 });
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map((_, i) => {
        const angle = (i / 6) * 360;
        const rad = (angle * Math.PI) / 180;
        const tx = Math.cos(rad) * 40;
        const ty = Math.sin(rad) * 40;
        return (
          <motion.div
            key={i}
            className="absolute w-1.5 h-1.5 rounded-full"
            style={{ left: '50%', top: '50%', backgroundColor: i % 2 === 0 ? '#FFD700' : '#FF6B35' }}
            initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
            animate={{ x: tx, y: ty, scale: 0, opacity: 0 }}
            transition={{ duration: 0.5, delay: i * 0.03, ease: "easeOut" }}
          />
        );
      })}
    </div>
  );
};

export const GameBoard = ({ 
  board, 
  size, 
  onCellClick, 
  winningCells, 
  disabled 
}) => {
  const [recentCell, setRecentCell] = useState(null);
  const { width: winW, height: winH } = useWindowSize();

  const isWinningCell = (row, col) => {
    return winningCells?.some(([r, c]) => r === row && c === col);
  };

  // Calculate cell size based on viewport - fits perfectly on any device
  const getCellPx = () => {
    const headerSpace = 180; // header + margins
    const padding = 60; // card padding + gaps
    const gapPx = size <= 3 ? 12 : 8;
    const maxBoardH = winH - headerSpace;
    const maxBoardW = Math.min(winW - 40, 900);
    const fromH = Math.floor((maxBoardH - padding - (size - 1) * gapPx) / size);
    const fromW = Math.floor((maxBoardW - padding - (size - 1) * gapPx) / size);
    const cell = Math.min(fromH, fromW);
    // Clamp between min and max
    if (size <= 3) return Math.max(60, Math.min(cell, 180));
    if (size <= 5) return Math.max(50, Math.min(cell, 120));
    if (size <= 9) return Math.max(36, Math.min(cell, 75));
    return Math.max(28, Math.min(cell, 55));
  };

  const cellPx = getCellPx();
  const symbolSize = Math.floor(cellPx * 0.75);

  const handleCellClick = (row, col) => {
    if (disabled || board[row][col]) return;
    setRecentCell(`${row}-${col}`);
    setTimeout(() => setRecentCell(null), 600);
    onCellClick(row, col);
  };

  return (
    <motion.div
      data-testid="game-board"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="relative glass-card-strong rounded-2xl p-3 sm:p-4 md:p-6"
    >
      <div
        className="grid"
        style={{ gridTemplateColumns: `repeat(${size}, ${cellPx}px)`, gap: size <= 3 ? 12 : 8, justifyContent: 'center' }}
      >
        {board.map((row, rowIndex) =>
          row.map((cell, colIndex) => {
            const cellKey = `${rowIndex}-${colIndex}`;
            const isRecent = recentCell === cellKey;
            const isWinner = isWinningCell(rowIndex, colIndex);
            
            return (
              <motion.button
                key={cellKey}
                data-testid={`game-cell-${rowIndex}-${colIndex}`}
                whileHover={!disabled && !cell ? { scale: 1.06, boxShadow: "0 0 20px rgba(255,215,0,0.3)" } : {}}
                whileTap={!disabled && !cell ? { scale: 0.93 } : {}}
                onClick={() => handleCellClick(rowIndex, colIndex)}
                disabled={disabled || !!cell}
                style={{ width: cellPx, height: cellPx }}
                className={`
                  flex items-center justify-center
                  border-2 rounded-xl
                  transition-all duration-200
                  relative overflow-hidden
                  ${!disabled && !cell 
                    ? "border-gold/20 hover:border-gold/50 cursor-pointer bg-gold/5 hover:bg-gold/10" 
                    : "cursor-default border-gold/15"}
                  ${isWinner ? "winner-cell" : cell ? "bg-gold/5" : "bg-gold/5"}
                `}
              >
                {/* Placement sparkle */}
                {isRecent && <PlaceParticles />}

                {/* X Symbol */}
                {cell === "X" && (
                  <motion.div
                    initial={{ scale: 0, rotate: -90 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                  >
                    <SymbolX size={symbolSize} />
                  </motion.div>
                )}

                {/* O Symbol */}
                {cell === "O" && (
                  <motion.div
                    initial={{ scale: 0, rotate: 90 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                  >
                    <SymbolO size={symbolSize} />
                  </motion.div>
                )}
              </motion.button>
            );
          })
        )}
      </div>
    </motion.div>
  );
};

export default GameBoard;
